import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useTabState from '../hooks/useTabState';
import { BASE, authFetch, titleCase } from '../constants';
import { useApp } from '../contexts/AppContext';
import { useToast } from '../contexts/ToastContext';
import PageLayout from '../components/ui/PageLayout';
import TabBar from '../components/ui/TabBar';
import KpiGrid from '../components/ui/KpiGrid';
import ActionButton from '../components/ui/ActionButton';

const API = `${BASE}/api/v1/ia-report`;

// -- Types --
interface FindingItem {
  id: number; finding_number: number; title: string; risk_rating: string;
  exception_count: number; total_financial_impact: number;
  affected_entities: string[]; exception_types: string[];
  failure_pattern_id: number | null; remediation_template_ids: number[];
  risk_score: number; sample_exceptions: any[]; department_breakdown: Record<string, number>;
  entity_breakdown: Record<string, { count: number; amount: number }>;
  resolution_summary: { resolved: number; pending: number; in_progress: number; auto_approved: number };
  condition: string; criteria: string; cause: string; effect: string;
  recommendation: string; management_response: string; priority: string;
  all_exception_ids: number[];
}

interface ReportSummary {
  id: number; report_period: string; status: string; generated_at: string;
  generated_by: string; finding_count: number; total_exceptions: number;
}

interface FullReport {
  id: number; organization_id: number; report_period: string;
  generated_at: string; generated_by: string; status: string;
  executive_summary: any; scope: any; overall_assessment: any;
  department_summary: Record<string, any>; remediation_roadmap: any;
  financial_impact: any; trending: any; metadata: any; findings: FindingItem[];
}

interface ControlsData {
  failure_pattern: any; framework_refs: any[]; control_objective: any; remediation_plan: any[];
}

// -- Helpers --
function fmt$(n: number): string {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtN(n: number): string { return n.toLocaleString(); }

const RISK_COLORS: Record<string, { color: string; bg: string }> = {
  CRITICAL: { color: '#ef4444', bg: 'rgba(239,68,68,0.06)' },
  HIGH: { color: '#f59e0b', bg: 'rgba(245,158,11,0.06)' },
  MEDIUM: { color: 'var(--navy-700)', bg: 'rgba(30,58,95,0.06)' },
  LOW: { color: 'var(--positive)', bg: 'rgba(34,197,94,0.06)' },
};

const ASSESS_COLORS: Record<string, { color: string; bg: string; label: string }> = {
  SIGNIFICANT_DEFICIENCIES: { color: '#ef4444', bg: 'rgba(239,68,68,0.06)', label: 'Significant Deficiencies' },
  DEFICIENCIES_NOTED: { color: '#f59e0b', bg: 'rgba(245,158,11,0.06)', label: 'Deficiencies Noted' },
  IMPROVEMENT_NEEDED: { color: 'var(--navy-700)', bg: 'rgba(30,58,95,0.06)', label: 'Improvement Needed' },
  CONTROLS_EFFECTIVE: { color: 'var(--positive)', bg: 'rgba(34,197,94,0.06)', label: 'Controls Effective' },
};

// -- Main Component --
export default function IAReport() {
  const navigate = useNavigate();
  const { authUser } = useApp();
  const { toast } = useToast();
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [report, setReport] = useState<FullReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expandedFinding, setExpandedFinding] = useState<number | null>(null);
  const [findingControls, setFindingControls] = useState<Record<number, ControlsData>>({});
  const IA_TABS = ['findings', 'roadmap', 'impact', 'trending'] as const;
  const [tab, setTab] = useTabState<typeof IA_TABS[number]>('tab', IA_TABS, 'findings');
  const [exporting, setExporting] = useState<'pdf' | 'docx' | null>(null);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/list?org_id=${authUser?.organization_id || 1}`);
      const list: ReportSummary[] = await res.json();
      setReports(list);

      const latest = list.find(r => r.status !== 'SUPERSEDED');
      if (latest) {
        const rRes = await authFetch(`${API}/${latest.id}`);
        setReport(await rRes.json());
      }
    } catch (e) {
      console.error('Failed to load reports', e);
      toast({ message: 'Failed to load reports. Please try again.', type: 'error' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);

  const generateReport = async () => {
    setGenerating(true);
    try {
      const res = await authFetch(`${API}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ org_id: authUser?.organization_id || 1, period: new Date().toISOString().slice(0, 7), generated_by: 'user' }),
      });
      if (res.ok) {
        const result = await res.json();
        const rRes = await authFetch(`${API}/${result.report_id}`);
        setReport(await rRes.json());
        loadReports();
      }
    } catch (e) {
      console.error('Generate failed', e);
      toast({ message: 'Report generation failed. Please try again.', type: 'error' });
    }
    setGenerating(false);
  };

  const regenerateReport = async () => {
    if (!report) return;
    setGenerating(true);
    try {
      const res = await authFetch(`${API}/${report.id}/regenerate`, { method: 'POST' });
      if (res.ok) {
        const result = await res.json();
        const rRes = await authFetch(`${API}/${result.new_report_id}`);
        setReport(await rRes.json());
        loadReports();
      }
    } catch (e) {
      console.error('Regenerate failed', e);
      toast({ message: 'Report regeneration failed. Please try again.', type: 'error' });
    }
    setGenerating(false);
  };

  const loadFindingControls = async (findingId: number) => {
    if (findingControls[findingId]) return;
    try {
      const res = await authFetch(`${API}/findings/${findingId}/controls`);
      if (res.ok) {
        const data = await res.json();
        setFindingControls(prev => ({ ...prev, [findingId]: data }));
      }
    } catch (e) {
      console.error('Failed to load controls', e);
      toast({ message: 'Failed to load controls data.', type: 'error' });
    }
  };

  const toggleFinding = (findingId: number) => {
    if (expandedFinding === findingId) {
      setExpandedFinding(null);
    } else {
      setExpandedFinding(findingId);
      loadFindingControls(findingId);
    }
  };

  const exportReport = async (format: 'pdf' | 'docx') => {
    if (!report) return;
    setExporting(format);
    try {
      const res = await authFetch(`${API}/export/${report.id}/${format}`);
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `IA_Report_${report.id}_${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(`${format.toUpperCase()} export failed`, e);
      toast({ message: `${format.toUpperCase()} export failed. Please try again.`, type: 'error' });
    }
    setExporting(null);
  };

  if (loading) {
    return <PageLayout title="Internal Audit Report"><div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading IA Report...</div></PageLayout>;
  }

  // -- No report: show generate prompt --
  if (!report) {
    return (
      <PageLayout title="Internal Audit Report" subtitle="Analyze all exceptions to identify systemic control failures, map to framework standards, and build a remediation plan.">
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 16, color: 'var(--text-secondary)', marginBottom: 20 }}>
            No report has been generated yet.
          </div>
          <ActionButton
            label={generating ? 'Generating...' : 'Generate IA Report'}
            onClick={generateReport}
            disabled={generating}
          />
          <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 12 }}>
            Analyzes ~1,500 exceptions across 6 frameworks. Takes a few seconds.
          </div>
        </div>
      </PageLayout>
    );
  }

  // -- Report exists: dashboard --
  const es = report.executive_summary;
  const assess = report.overall_assessment;
  const assessStyle = ASSESS_COLORS[assess?.level] || ASSESS_COLORS.CONTROLS_EFFECTIVE;

  return (
    <PageLayout
      title="Internal Audit Report"
      subtitle={`Period: ${report.report_period} | Generated: ${new Date(report.generated_at).toLocaleString()} | Status: ${report.status}${report.metadata?.generation_time_ms ? ` | ${report.metadata.generation_time_ms}ms` : ''}`}
      actions={[
        { label: exporting === 'pdf' ? 'Exporting...' : 'PDF', onClick: () => exportReport('pdf'), variant: 'secondary' },
        { label: exporting === 'docx' ? 'Exporting...' : 'DOCX', onClick: () => exportReport('docx'), variant: 'secondary' },
        { label: generating ? 'Regenerating...' : 'Regenerate', onClick: regenerateReport },
      ]}
    >
      {/* Executive Summary */}
      <div style={{
        padding: 20, borderRadius: 8, marginBottom: 16,
        background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', marginBottom: 12 }}>
          Executive Summary
        </div>

        {/* Assessment badge */}
        <div style={{
          display: 'inline-block', padding: '6px 16px', borderRadius: 6, marginBottom: 16,
          background: assessStyle.bg, border: `1px solid ${assessStyle.color}30`,
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: assessStyle.color }}>{assessStyle.label}</span>
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 10 }}>{assess?.summary}</span>
        </div>

        {/* KPI strip */}
        <KpiGrid columns={5} items={[
          { label: 'Total Findings', value: es?.total_findings || 0 },
          { label: 'By Risk Rating', value: (['critical', 'high', 'medium', 'low'] as const).map(r => `${es?.by_rating?.[r] || 0} ${r.toUpperCase()}`).filter(s => !s.startsWith('0')).join(', ') || '-' },
          { label: 'Exceptions Analyzed', value: fmtN(es?.total_exceptions_analyzed || 0) },
          { label: 'Gross Exception Value', value: fmt$(es?.total_financial_exposure || es?.gross_exception_value || 0), color: '#ef4444' },
          { label: es?.health_score != null ? 'Health Score' : 'Potential Savings', value: es?.health_score != null ? `${es.health_score.toFixed(1)}%` : fmt$(report.financial_impact?.potential_savings || 0), color: 'var(--positive)' },
        ]} />
      </div>

      {/* Tabs */}
      <TabBar
        tabs={[
          { key: 'findings', label: `Findings (${report.findings.length})` },
          { key: 'roadmap', label: 'Remediation Roadmap' },
          { key: 'impact', label: 'Financial Impact' },
          { key: 'trending', label: 'Trending' },
        ]}
        active={tab}
        onChange={t => setTab(t as any)}
      />

      {/* Tab content */}
      {tab === 'findings' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {report.findings.map(f => {
            const rc = RISK_COLORS[f.risk_rating] || RISK_COLORS.LOW;
            const isExpanded = expandedFinding === f.id;
            const controls = findingControls[f.id];
            const rs = f.resolution_summary || { resolved: 0, pending: 0, in_progress: 0, auto_approved: 0 };
            const totalActions = rs.resolved + rs.pending + rs.in_progress;

            return (
              <div key={f.id} style={{
                background: 'var(--bg-card)', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                border: isExpanded ? `1px solid ${rc.color}40` : '1px solid var(--border-subtle)',
              }}>
                {/* Finding header */}
                <div onClick={() => toggleFinding(f.id)} style={{
                  padding: '14px 18px', cursor: 'pointer',
                  display: 'grid', gridTemplateColumns: '40px 1fr 100px 120px 100px', gap: 12, alignItems: 'center',
                }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>#{f.finding_number}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-900)' }}>{f.title}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 2 }}>
                      {f.exception_types.map(t => titleCase(t)).join(', ')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <span style={{
                      padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                      background: rc.bg, color: rc.color, textAlign: 'center',
                    }}>{f.risk_rating}</span>
                    {f.priority && (
                      <span style={{
                        padding: '3px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700,
                        background: 'rgba(0,0,0,0.04)', color: f.priority === 'IMMEDIATE' ? '#ef4444' : f.priority === 'HIGH' ? '#f59e0b' : 'var(--navy-700)',
                      }}>{f.priority}</span>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy-900)', fontFamily: 'var(--font-mono)' }}>{fmtN(f.exception_count)}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-hint)' }}>exceptions</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#ef4444', fontFamily: 'var(--font-mono)' }}>{fmt$(f.total_financial_impact)}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-hint)' }}>exposure</div>
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div style={{ padding: '0 18px 18px', borderTop: '1px solid var(--border-subtle)' }}>
                    {/* Summary row */}
                    <div style={{ display: 'flex', gap: 16, padding: '12px 0', fontSize: 11, color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                      <span>Entities: <span style={{ color: 'var(--navy-800)' }}>{f.affected_entities.join(', ') || 'All'}</span></span>
                      <span>Score: <span style={{ color: 'var(--navy-900)', fontWeight: 600 }}>{f.risk_score}</span></span>
                      {f.all_exception_ids?.length > 0 && (
                        <span>Exception IDs: <span style={{ color: 'var(--navy-800)' }}>{f.all_exception_ids.length} linked</span></span>
                      )}
                      {f.remediation_template_ids.length > 0 && (
                        <span>Remediations: <span style={{ color: 'var(--positive)' }}>{f.remediation_template_ids.length}</span></span>
                      )}
                    </div>

                    {/* IIA/PCAOB Standard Structure */}
                    {f.condition && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', marginBottom: 8 }}>
                          IIA Standard Finding Structure
                        </div>
                        {([
                          { label: 'CONDITION', value: f.condition, color: '#ef4444', desc: 'What IS happening' },
                          { label: 'CRITERIA', value: f.criteria, color: 'var(--navy-700)', desc: 'What SHOULD happen' },
                          { label: 'CAUSE', value: f.cause, color: '#f59e0b', desc: 'Why it happened' },
                          { label: 'EFFECT', value: f.effect, color: '#ef4444', desc: 'Impact' },
                          { label: 'RECOMMENDATION', value: f.recommendation, color: 'var(--positive)', desc: 'What to do' },
                          { label: 'MANAGEMENT RESPONSE', value: f.management_response, color: 'var(--navy-800)', desc: 'Management input' },
                        ] as const).map(section => section.value ? (
                          <div key={section.label} style={{ padding: '8px 12px', marginBottom: 4, borderRadius: 6, background: 'rgba(0,0,0,0.02)', borderLeft: `3px solid ${section.color}` }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                              <span style={{ fontSize: 10, fontWeight: 700, color: section.color }}>{section.label}</span>
                              <span style={{ fontSize: 9, color: 'var(--text-hint)' }}>{section.desc}</span>
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{section.value}</div>
                          </div>
                        ) : null)}
                      </div>
                    )}

                    {/* Root Cause */}
                    {controls?.failure_pattern && (
                      <div style={{ padding: 12, marginBottom: 12, borderRadius: 6, background: 'rgba(239,68,68,0.03)', border: '1px solid rgba(239,68,68,0.08)' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', marginBottom: 6 }}>Root Cause</div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{controls.failure_pattern.root_cause}</div>
                        {controls.failure_pattern.root_cause_detail?.length > 0 && (
                          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                            {controls.failure_pattern.root_cause_detail.map((d: string, i: number) => (
                              <li key={i} style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>{d}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}

                    {/* Framework refs */}
                    {controls?.framework_refs?.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--navy-800)', textTransform: 'uppercase', marginBottom: 6 }}>Framework References</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {controls.framework_refs.map((ref: any, i: number) => (
                            <span key={i} style={{ padding: '3px 10px', background: 'rgba(30,58,95,0.04)', border: '1px solid rgba(30,58,95,0.08)', borderRadius: 6, fontSize: 10 }}>
                              <span style={{ color: 'var(--navy-800)', fontWeight: 700 }}>{ref.framework_code}-{ref.principle_code}</span>
                              <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>{ref.principle_name}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Control objective */}
                    {controls?.control_objective && (
                      <div style={{ padding: 12, marginBottom: 12, borderRadius: 6, background: 'rgba(30,58,95,0.03)', border: '1px solid rgba(30,58,95,0.08)' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--navy-800)', textTransform: 'uppercase', marginBottom: 6 }}>Expected Control</div>
                        <div style={{ fontSize: 12, color: 'var(--navy-900)', fontWeight: 600 }}>{controls.control_objective.objective_code}: {controls.control_objective.objective_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>{controls.control_objective.description}</div>
                      </div>
                    )}

                    {/* Remediation plan */}
                    {controls?.remediation_plan?.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--positive)', textTransform: 'uppercase', marginBottom: 6 }}>Remediation Plan</div>
                        {controls.remediation_plan.map((r: any, i: number) => (
                          <div key={i} style={{ padding: 10, marginBottom: 6, borderRadius: 6, background: 'rgba(34,197,94,0.03)', border: '1px solid rgba(34,197,94,0.1)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: 12, color: 'var(--navy-900)', fontWeight: 600 }}>{r.template_name}</span>
                              <div style={{ display: 'flex', gap: 8 }}>
                                {r.implementation_complexity && (
                                  <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: 'rgba(0,0,0,0.04)', color: 'var(--text-secondary)' }}>{r.implementation_complexity}</span>
                                )}
                                {r.expected_reduction_pct && (
                                  <span style={{ fontSize: 10, color: 'var(--positive)' }}>-{r.expected_reduction_pct}%</span>
                                )}
                              </div>
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>{r.description}</div>
                            {r.steps?.length > 0 && (
                              <div style={{ marginTop: 8 }}>
                                {r.steps.map((s: any) => (
                                  <div key={s.step_number} style={{ display: 'flex', gap: 8, fontSize: 10, padding: '3px 0', color: 'var(--text-hint)' }}>
                                    <span style={{ color: 'var(--positive)', fontWeight: 600, minWidth: 20 }}>S{s.step_number}</span>
                                    <span style={{ flex: 1 }}>{s.action}</span>
                                    <span style={{ color: 'var(--navy-800)', minWidth: 80, textAlign: 'right' }}>{s.owner_role}</span>
                                    <span style={{ minWidth: 40, textAlign: 'right' }}>{s.target_days}d</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Resolution progress */}
                    <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', marginBottom: 6 }}>Resolution Progress</div>
                        <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
                          <span>Resolved: <span style={{ color: 'var(--positive)', fontWeight: 600 }}>{rs.resolved}</span></span>
                          <span>Pending: <span style={{ color: 'var(--warning)', fontWeight: 600 }}>{rs.pending}</span></span>
                          <span>In Progress: <span style={{ color: 'var(--navy-700)', fontWeight: 600 }}>{rs.in_progress}</span></span>
                          <span>Auto-Approved: <span style={{ color: 'var(--navy-800)', fontWeight: 600 }}>{rs.auto_approved}</span></span>
                        </div>
                        {totalActions > 0 && (
                          <div style={{ marginTop: 6, height: 6, borderRadius: 3, background: 'var(--border-subtle)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', borderRadius: 3, background: 'var(--positive)', width: `${(rs.resolved / totalActions) * 100}%`, transition: 'width 0.3s' }} />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Department + Entity breakdown side by side */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      {Object.keys(f.department_breakdown).length > 0 && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', marginBottom: 6 }}>By Department</div>
                          {Object.entries(f.department_breakdown)
                            .sort(([, a], [, b]) => b - a)
                            .map(([dept, count]) => {
                              const maxCount = Math.max(...Object.values(f.department_breakdown));
                              return (
                                <div key={dept} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                                  <span style={{ fontSize: 10, color: 'var(--text-hint)', minWidth: 100, textAlign: 'right' }}>{titleCase(dept)}</span>
                                  <div style={{ flex: 1, height: 12, background: 'var(--border-subtle)', borderRadius: 2, overflow: 'hidden' }}>
                                    <div style={{ width: `${(count / maxCount) * 100}%`, height: '100%', background: 'rgba(30,58,95,0.15)', borderRadius: 2 }} />
                                  </div>
                                  <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--navy-900)', minWidth: 30, textAlign: 'right' }}>{count}</span>
                                </div>
                              );
                            })}
                        </div>
                      )}

                      {Object.keys(f.entity_breakdown).length > 0 && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', marginBottom: 6 }}>By Entity</div>
                          {Object.entries(f.entity_breakdown).map(([entity, data]: [string, any]) => (
                            <div key={entity} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '3px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                              <span style={{ color: 'var(--navy-800)' }}>{entity}</span>
                              <span style={{ color: 'var(--text-secondary)' }}>{data.count} exc | {fmt$(data.amount)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Sample exceptions */}
                    {f.sample_exceptions?.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', marginBottom: 6 }}>Top Exceptions by Amount</div>
                        {f.sample_exceptions.map((s: any) => (
                          <div key={s.exception_id} style={{
                            display: 'flex', gap: 12, alignItems: 'center', padding: '6px 0',
                            borderBottom: '1px solid var(--border-subtle)', fontSize: 11,
                          }}>
                            <span
                              onClick={() => navigate(`/exception-tracker/${s.exception_id}`)}
                              style={{ color: 'var(--navy-800)', cursor: 'pointer', fontFamily: 'var(--font-mono)', minWidth: 50 }}
                            >#{s.exception_id}</span>
                            <span style={{ color: 'var(--navy-800)', minWidth: 120 }}>{titleCase(s.exception_type)}</span>
                            <span style={{ color: 'var(--navy-900)', fontWeight: 600, fontFamily: 'var(--font-mono)', minWidth: 100, textAlign: 'right' }}>{fmt$(s.amount)}</span>
                            <span style={{ color: 'var(--text-hint)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {s.description?.substring(0, 100)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'roadmap' && report.remediation_roadmap && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {Object.entries(report.remediation_roadmap.phases || {}).map(([key, phase]: [string, any]) => (
            <div key={key} style={{
              background: 'var(--bg-card)', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              border: '1px solid var(--border-subtle)', overflow: 'hidden',
            }}>
              <div style={{
                padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                borderBottom: phase.items.length > 0 ? '1px solid var(--border-subtle)' : 'none',
                background: key === 'ongoing' ? 'rgba(30,58,95,0.02)' : 'transparent',
              }}>
                <div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy-900)' }}>{phase.name}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 10 }}>{phase.timeline}</span>
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: phase.items.length > 0 ? 'var(--navy-800)' : 'var(--text-hint)' }}>
                  {phase.items.length} items
                </span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', padding: phase.items.length > 0 ? '8px 18px 0' : '0 18px 12px' }}>
                {phase.description}
              </div>
              {phase.items.length > 0 && (
                <div style={{ padding: '8px 18px 14px' }}>
                  {phase.items.map((item: any, i: number) => {
                    const irc = RISK_COLORS[item.risk_rating] || RISK_COLORS.LOW;
                    return (
                      <div key={i} style={{
                        display: 'flex', gap: 10, alignItems: 'center', padding: '8px 0',
                        borderBottom: i < phase.items.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                      }}>
                        <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-hint)', minWidth: 24 }}>#{item.finding_number}</span>
                        <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700, background: irc.bg, color: irc.color }}>{item.risk_rating}</span>
                        <span style={{ flex: 1, color: 'var(--text-secondary)' }}>{item.remediation_name}</span>
                        {item.estimated_cost && (
                          <span style={{ fontSize: 10, color: 'var(--text-hint)' }}>{item.estimated_cost}</span>
                        )}
                        {item.expected_reduction_pct && (
                          <span style={{ fontSize: 10, color: 'var(--positive)', fontWeight: 600 }}>-{item.expected_reduction_pct}%</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'impact' && report.financial_impact && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* By Risk Rating */}
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid var(--border-subtle)', padding: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', marginBottom: 12 }}>By Risk Rating</div>
            {Object.entries(report.financial_impact.by_risk_rating || {}).map(([rating, amount]: [string, any]) => {
              const rc = RISK_COLORS[rating] || RISK_COLORS.LOW;
              const pct = report.financial_impact.total_exposure > 0 ? (amount / report.financial_impact.total_exposure) * 100 : 0;
              return (
                <div key={rating} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: rc.color, minWidth: 70 }}>{rating}</span>
                  <div style={{ flex: 1, height: 16, background: 'var(--border-subtle)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: rc.color, opacity: 0.4, borderRadius: 3 }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy-900)', fontFamily: 'var(--font-mono)', minWidth: 90, textAlign: 'right' }}>{fmt$(amount)}</span>
                  <span style={{ fontSize: 10, color: 'var(--text-hint)', minWidth: 40, textAlign: 'right' }}>{pct.toFixed(1)}%</span>
                </div>
              );
            })}
          </div>

          {/* By Entity */}
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid var(--border-subtle)', padding: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', marginBottom: 12 }}>By Entity</div>
            {Object.entries(report.financial_impact.by_entity || {}).map(([entity, amount]: [string, any]) => {
              const pct = report.financial_impact.total_exposure > 0 ? (amount / report.financial_impact.total_exposure) * 100 : 0;
              return (
                <div key={entity} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 11, color: 'var(--navy-800)', minWidth: 70 }}>{entity}</span>
                  <div style={{ flex: 1, height: 16, background: 'var(--border-subtle)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: 'var(--navy-700)', opacity: 0.3, borderRadius: 3 }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy-900)', fontFamily: 'var(--font-mono)', minWidth: 90, textAlign: 'right' }}>{fmt$(amount)}</span>
                  <span style={{ fontSize: 10, color: 'var(--text-hint)', minWidth: 40, textAlign: 'right' }}>{pct.toFixed(1)}%</span>
                </div>
              );
            })}
          </div>

          {/* By Cycle */}
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid var(--border-subtle)', padding: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', marginBottom: 12 }}>By Business Cycle</div>
            {Object.entries(report.financial_impact.by_cycle || {})
              .sort(([, a]: any, [, b]: any) => b - a)
              .map(([cycle, amount]: [string, any]) => (
                <div key={cycle} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                  <span style={{ color: 'var(--navy-800)' }}>{cycle}</span>
                  <span style={{ fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--navy-900)' }}>{fmt$(amount)}</span>
                </div>
              ))}
          </div>

          {/* Department summary */}
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid var(--border-subtle)', padding: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', marginBottom: 12 }}>By Department</div>
            {Object.entries(report.department_summary || {})
              .sort(([, a]: any, [, b]: any) => b.total_impact - a.total_impact)
              .map(([dept, info]: [string, any]) => {
                const drc = RISK_COLORS[info.top_risk_rating] || RISK_COLORS.LOW;
                return (
                  <div key={dept} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)', flex: 1 }}>{titleCase(dept)}</span>
                    <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700, background: drc.bg, color: drc.color }}>{info.top_risk_rating}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-hint)', minWidth: 40, textAlign: 'right' }}>{info.exception_count} exc</span>
                    <span style={{ fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--navy-900)', minWidth: 80, textAlign: 'right' }}>{fmt$(info.total_impact)}</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Trending tab */}
      {tab === 'trending' && report.trending && (() => {
        const t = report.trending;
        const vel = t.resolution_velocity || {};
        const conc = t.risk_concentration || {};
        const deptRisk = t.department_risk_ranking || [];
        const dailyVol = t.daily_volume || [];
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Resolution Velocity */}
            <div style={{ background: 'var(--bg-card)', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid var(--border-subtle)', padding: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', marginBottom: 12 }}>Resolution Velocity</div>
              <KpiGrid columns={3} items={[
                { label: 'Resolved', value: vel.resolved || 0, color: 'var(--positive)' },
                { label: 'Pending', value: vel.pending || 0, color: 'var(--warning)' },
                { label: 'In Progress', value: vel.in_progress || 0, color: 'var(--navy-700)' },
              ]} />
              {vel.resolution_rate !== undefined && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Resolution Rate</span>
                    <span style={{ color: 'var(--positive)', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{vel.resolution_rate.toFixed(1)}%</span>
                  </div>
                  <div style={{ height: 8, borderRadius: 4, background: 'var(--border-subtle)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 4, background: 'var(--positive)', width: `${Math.min(vel.resolution_rate, 100)}%` }} />
                  </div>
                </div>
              )}
            </div>

            {/* Risk Concentration */}
            <div style={{ background: 'var(--bg-card)', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid var(--border-subtle)', padding: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', marginBottom: 12 }}>Risk Concentration</div>
              <KpiGrid columns={2} items={[
                { label: 'Critical Exposure', value: fmt$(conc.critical_exposure || 0), color: '#ef4444' },
                { label: 'Total Exposure', value: fmt$(conc.total_exposure || 0) },
              ]} />
              {conc.critical_pct !== undefined && (
                <div style={{ padding: 10, borderRadius: 6, background: conc.critical_pct > 50 ? 'rgba(239,68,68,0.04)' : 'rgba(34,197,94,0.04)', border: `1px solid ${conc.critical_pct > 50 ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)'}` }}>
                  <span style={{ fontSize: 11, color: conc.critical_pct > 50 ? '#ef4444' : 'var(--positive)', fontWeight: 600 }}>
                    {conc.critical_pct.toFixed(1)}% of total exposure rated CRITICAL
                  </span>
                </div>
              )}
            </div>

            {/* Department Risk Ranking */}
            <div style={{ background: 'var(--bg-card)', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid var(--border-subtle)', padding: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', marginBottom: 12 }}>Department Risk Ranking</div>
              {deptRisk.map((d: any, i: number) => {
                const maxScore = deptRisk[0]?.risk_score || 1;
                return (
                  <div key={d.department} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', minWidth: 18 }}>#{i + 1}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)', minWidth: 120 }}>{titleCase(d.department)}</span>
                    <div style={{ flex: 1, height: 12, background: 'var(--border-subtle)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ width: `${(d.risk_score / maxScore) * 100}%`, height: '100%', background: i < 3 ? 'rgba(239,68,68,0.3)' : 'rgba(30,58,95,0.15)', borderRadius: 2 }} />
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--navy-900)', fontFamily: 'var(--font-mono)', minWidth: 40, textAlign: 'right' }}>{d.risk_score}</span>
                  </div>
                );
              })}
            </div>

            {/* Daily Volume */}
            <div style={{ background: 'var(--bg-card)', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid var(--border-subtle)', padding: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', marginBottom: 12 }}>Daily Exception Volume</div>
              {dailyVol.length === 0 ? (
                <div style={{ fontSize: 11, color: 'var(--text-hint)', padding: 12, textAlign: 'center' }}>No daily volume data available</div>
              ) : (
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {dailyVol.slice(0, 15).map((d: any) => (
                    <div key={d.date} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '4px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                      <span style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>{d.date}</span>
                      <span style={{ color: 'var(--navy-800)', fontWeight: 600 }}>{d.count} exc</span>
                      <span style={{ color: 'var(--navy-900)', fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{fmt$(d.amount || 0)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {tab === 'trending' && !report.trending && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 12 }}>
          No trending data available. Regenerate the report to compute trending analysis.
        </div>
      )}

      {/* Scope footer */}
      {report.scope && (
        <div style={{ marginTop: 24, padding: 16, borderRadius: 8, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', marginBottom: 8 }}>Scope & Methodology</div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 6 }}>{report.scope.methodology}</div>
          <div style={{ fontSize: 11, color: 'var(--text-hint)', lineHeight: 1.5 }}>{report.scope.limitations}</div>
          <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 10, color: 'var(--text-hint)' }}>
            <span>Entities: {report.scope.entities_covered?.length || 0}</span>
            <span>Frameworks: {report.scope.frameworks?.join(', ')}</span>
            <span>JEs Reviewed: {fmtN(report.scope.total_jes_reviewed || 0)}</span>
            <span>Exception Types: {report.scope.exception_types_analyzed || 0}</span>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
